/**
 * Local Storage Service for Favorites and Recent Contacts
 */

import { ref, computed } from 'vue';
import { branding } from './oauth2-config';

// Use configurable storage prefix for multi-tenant support
const FAVORITES_KEY = `${branding.storagePrefix}_favorites`;
const RECENT_KEY = `${branding.storagePrefix}_recent`;
const SEARCH_HISTORY_KEY = `${branding.storagePrefix}_search_history`;
const MAX_RECENT = 10;
const MAX_SEARCH_HISTORY = 5;

// Reactive state
const favorites = ref(loadFavorites());
const recentContacts = ref(loadRecent());
const searchHistory = ref(loadSearchHistory());

// ============================================
// FAVORITES
// ============================================

function loadFavorites() {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_KEY)) || [];
  } catch {
    return [];
  }
}

function saveFavorites() {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites.value));
}

/**
 * Toggle favorite status for a contact
 * @param {string} email - Contact email (primary key)
 */
export function toggleFavorite(email) {
  const index = favorites.value.indexOf(email);
  if (index > -1) {
    favorites.value.splice(index, 1);
  } else {
    favorites.value.push(email);
  }
  saveFavorites();
}

/**
 * Check if contact is a favorite
 * @param {string} email - Contact email
 * @returns {boolean}
 */
export function isFavorite(email) {
  return favorites.value.includes(email);
}

/**
 * Get all favorite emails
 * @returns {string[]}
 */
export function getFavorites() {
  return favorites.value;
}

// ============================================
// RECENT CONTACTS
// ============================================

function loadRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
  } catch {
    return [];
  }
}

function saveRecent() {
  localStorage.setItem(RECENT_KEY, JSON.stringify(recentContacts.value));
}

/**
 * Add contact to recent list (on click/interaction)
 * @param {Object} contact - Contact object with email
 */
export function addToRecent(contact) {
  if (!contact?.email) return;
  
  // Remove if already exists
  const index = recentContacts.value.findIndex(c => c.email === contact.email);
  if (index > -1) {
    recentContacts.value.splice(index, 1);
  }
  
  // Add to front
  recentContacts.value.unshift({
    email: contact.email,
    display_name: contact.display_name,
    job_title: contact.job_title,
    company_name: contact.company_name,
    entra_id: contact.entra_id,
    is_entra_user: contact.is_entra_user,
    mobile_phone: contact.mobile_phone,
    business_phones: contact.business_phones,
    timestamp: Date.now()
  });
  
  // Keep only MAX_RECENT
  if (recentContacts.value.length > MAX_RECENT) {
    recentContacts.value = recentContacts.value.slice(0, MAX_RECENT);
  }
  
  saveRecent();
}

/**
 * Get recent contacts
 * @returns {Object[]}
 */
export function getRecentContacts() {
  return recentContacts.value;
}

/**
 * Clear recent contacts
 */
export function clearRecent() {
  recentContacts.value = [];
  saveRecent();
}

// ============================================
// SEARCH HISTORY
// ============================================

function loadSearchHistory() {
  try {
    return JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY)) || [];
  } catch {
    return [];
  }
}

function saveSearchHistory() {
  localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(searchHistory.value));
}

/**
 * Add search term to history
 * @param {string} term - Search term
 */
export function addToSearchHistory(term) {
  if (!term || term.trim().length < 2) return;
  
  const trimmed = term.trim();
  
  // Remove if already exists
  const index = searchHistory.value.indexOf(trimmed);
  if (index > -1) {
    searchHistory.value.splice(index, 1);
  }
  
  // Add to front
  searchHistory.value.unshift(trimmed);
  
  // Keep only MAX
  if (searchHistory.value.length > MAX_SEARCH_HISTORY) {
    searchHistory.value = searchHistory.value.slice(0, MAX_SEARCH_HISTORY);
  }
  
  saveSearchHistory();
}

/**
 * Get search history
 * @returns {string[]}
 */
export function getSearchHistory() {
  return searchHistory.value;
}

/**
 * Clear search history
 */
export function clearSearchHistory() {
  searchHistory.value = [];
  saveSearchHistory();
}

// ============================================
// MONITOR MODE
// ============================================

const MONITOR_MODE_KEY = `${branding.storagePrefix}_monitor_mode`;

/**
 * Get monitor mode state
 * @returns {boolean}
 */
export function getMonitorMode() {
  try {
    return localStorage.getItem(MONITOR_MODE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Set monitor mode state
 * @param {boolean} enabled
 */
export function setMonitorMode(enabled) {
  try {
    localStorage.setItem(MONITOR_MODE_KEY, enabled ? 'true' : 'false');
  } catch {}
}

// ============================================
// CALL PREFERENCES (Favorite call method per contact — DB-backed)
// ============================================

import { authenticatedFetch } from './oauth2-config';

const API_URL_LS = process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__';

const callPreferences = ref({});
let callPrefsLoaded = false;

/**
 * Load call preferences from server (call once on app init)
 */
export async function loadCallPreferencesFromServer() {
  try {
    const response = await authenticatedFetch(`${API_URL_LS}/api/contacts/call-preferences`);
    if (response.ok) {
      callPreferences.value = await response.json();
      callPrefsLoaded = true;
    }
  } catch (error) {
    console.error('Error loading call preferences:', error);
  }
}

/**
 * Set preferred call method for a contact (persisted to DB)
 * @param {string} email - Contact email
 * @param {string} method - 'teams', 'business', 'mobile'
 */
export async function setCallPreference(email, method) {
  // Optimistic update
  callPreferences.value[email] = method;
  try {
    await authenticatedFetch(`${API_URL_LS}/api/contacts/call-preferences/${encodeURIComponent(email)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ method })
    });
  } catch (error) {
    console.error('Error setting call preference:', error);
  }
}

/**
 * Get preferred call method for a contact
 * @param {string} email - Contact email
 * @returns {string|null} - 'teams', 'business', 'mobile', or null
 */
export function getCallPreference(email) {
  return callPreferences.value[email] || null;
}

/**
 * Remove preferred call method for a contact (persisted to DB)
 * @param {string} email - Contact email
 */
export async function removeCallPreference(email) {
  delete callPreferences.value[email];
  try {
    await authenticatedFetch(`${API_URL_LS}/api/contacts/call-preferences/${encodeURIComponent(email)}`, {
      method: 'DELETE'
    });
  } catch (error) {
    console.error('Error removing call preference:', error);
  }
}

// ============================================
// EXPORTS
// ============================================

export {
  favorites,
  recentContacts,
  searchHistory,
  callPreferences
};
